import React, {useCallback, useEffect, useState} from 'react';
import {ThemeProvider, createTheme} from '@mui/material/styles';
import {DataGridPro} from '@mui/x-data-grid-pro';
import {CssBaseline, Grid, Typography, IconButton} from '@mui/material';
import {ReactComponent as OpenFullScreen} from '../../../../assests/svg/openFullScreen.svg';
import AurButton from '../../../common/aur-button/AurButton';
import {useNavigate} from 'react-router-dom';
import TallyDetailsExportDialog from '../TallyDetailsExportDialog';
import {RootState} from '../../../../redux/store';
import {useDispatch, useSelector} from 'react-redux';
import {getExportList} from '../../../../api/EcaManagementService';
import {t} from 'i18next';
import {
  formatNumberWithComma,
  getUTCDateTimeForDisplay,
} from '../../../../utils/utils';
import {
  setExportId,
  setExportName,
} from '../../../../redux/slices/addExportSlice';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {AurLightTooltip} from '../../../common/aur-light-tooltip/aur-light-tooltip';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';

import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
interface Report {
  id: number;
  completedOn: string;
  destination: string;
  documentCount: number;
  exportId: string;
  loadedToDestination: string;
  userName: string;
}

const EcaExports = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const [isOpen, setIsOpen] = useState(false);
  const [exportReport, setExportReport] = useState<Report[]>([]);
  const [totalItems, setTotalItems] = useState<number>();

  const {projectId} = useSelector((state: RootState) => state.reports);
  const {showExportManagement} = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const lightTheme = createTheme({
    palette: {
      mode: 'light',
    },
  });
  const {hasExportPerm} = usePermissionsByPage(PAGE_NAME.ECA_OVERVIEW);

  const handleRowClick = (data: any) => {
    dispatch(setExportId(data.exportId));
    dispatch(setExportName(data.exportName));
    navigate('/users/exportdetails');
  };

  const columns = [
    {
      field: 'exportName',
      headerName: t('eca.exportManagement.exportName'),
      minWidth: 214,
      flex: 1,
      sortable: false,
      renderCell: (params: any) => {
        const promotionNumber = params?.row?.promotionNumber?.trim() || '--';
        const showPromotionNumber =
          params.row?.exportType?.toLowerCase() !== 'analytics';
        return (
          <div>
            <Typography className="typo-text body3_size text-color-89A6FF space-5" component="div">
              <Typography
                component="span"
                className={`typo-text body3_size text-color-3845AD ${!showPromotionNumber && `space-10`}`}
              >
                {params.value}
              </Typography>
            </Typography>
            {showPromotionNumber && (
              <AurTooltip
                title={promotionNumber !== '--' ? promotionNumber : ''}
              >
                <Typography className="typo-text body4_size text-color-A0AEC2 promotion-number-text" component="p">
                  {t('common.promotionNumber')}: {promotionNumber}
                </Typography>
              </AurTooltip>
            )}
          </div>
        );
      },
    },
    {
      field: 'exportDateTime',
      headerName: t('ecaHomepageModals.exportModal.table.completedOn'),
      width: 180,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <AurLightTooltip title={t('common.yearMonthDate')}>
            <Typography className="typo-text body4_size space-date inline-flex">
              {getUTCDateTimeForDisplay(params.value)}
            </Typography>
          </AurLightTooltip>
        );
      },
    },
    {
      field: 'destination',
      headerName: t('ecaHomepageModals.exportModal.table.destination'),
      sortable: false,
      width: 180,
      renderCell: () => 'Relativity',
    },
    {
      field: 'exportedBy',
      sortable: false,
      headerName: t('ecaHomepageModals.exportModal.table.userName'),
      width: 150,
    },
    {
      field: 'totalDocExported',
      sortable: false,
      headerName: t('ecaHomepageModals.exportModal.table.documentCount'),
      width: 200,
      renderCell: (params: any) => {
        const formattedCount = formatNumberWithComma(params.value);

        return (
          <Typography className="typo-text body4_size space-date">
            {formattedCount}
          </Typography>
        );
      },
    },
    {
      field: 'loadedOnTime',
      sortable: false,
      headerName: t('ecaHomepageModals.exportModal.table.loadedToDestination'),
      width: 300,
      renderHeader: () => {
        return (
          <div className="custom-header">
            <span>{t('ecaHomepageModals.exportModal.loadedTo')}</span>
            <br />
            <span>{t('ecaHomepageModals.exportModal.table.destination')}</span>
          </div>
        );
      },
      renderCell: (params: any) => {
        const formattedDate = params.value
          ? getUTCDateTimeForDisplay(params.value)
          : '-';

        return (
          <AurLightTooltip title={t('common.yearMonthDate')}>
            <Typography className="typo-text body4_size space-date inline-flex">
              {formattedDate}
            </Typography>
          </AurLightTooltip>
        );
      },
    },
  ];

  const getExportDataList = useCallback(async () => {
    const params = `?clientId=${clientId}&projectId=${projectId}`;
    try {
      const results = await getExportList(params);
      const exportData = results?.data?.data;
      const processedData = exportData.map((item: Report, index: number) => ({
        ...item,
        id: index + 1,
      }));
      setExportReport(processedData);
      setTotalItems(results.data?.meta?.pagination.totalItems);
    } catch (e) {
      console.log(e);
    }
  }, [clientId, projectId]);

  useEffect(() => {
    getExportDataList();
  }, [getExportDataList]);

  useEffect(() => {
  document
    .querySelector<HTMLElement>(
      '[data-field="exportName"][role="columnheader"]'
    )
    ?.setAttribute('tabindex', '-1');
  }, [exportReport]);

  if (!hasExportPerm) return null;

  return (
    <Grid item xs={12}>
      <div className="doc-column em-section">
        <div className="doc-header">
          <div className="d-flex">
            <Typography className="typo-text subhead4_size">
              {t('common.exports')} ({totalItems})
            </Typography>
          </div>
          <div className="icon-sec ">
            {showExportManagement && (
              <AurButton
                data-testid="search-button"
                onClick={() => {
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.CLICK,
                    click: {
                      name: t('gaTracking.ecaHomePage.goToExportManagement'),
                      region: GA_REGIONS.BODY,
                      type: GA_CLICK_TYPES.BUTTON,
                      action: `${window.location.host}/eci-home-page/export-management`,
                    },
                    subPage: {
                      subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
                        label: t('gaTracking.ecaHomePage.exports'),
                      }),
                      subpage_type: GA_SUBPAGE_TYPE.MODAL,
                    },
                  });
                  navigate('/eci-home-page/export-management');
                }}
                type="continue"
                size="Medium"
                title={t('ecaHomepageModals.exportModal.goToExportManagement')}
                ariaLabel={t(
                  'ecaHomepageModals.exportModal.goToExportManagement'
                )}
                label={t('ecaHomepageModals.exportModal.goToExportManagement')}
                classNames="typo-text buttonMedium_size em-header"
              />
            )}
            <span id="exports-expand-hint" className="sr-only">
              {t('eca.ecaHomepage.ecaExports')}
            </span>
            <IconButton
              size="small"
              onClick={() => {
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.CLICK,
                  click: {
                    name: t('gaTracking.ecaHomePage.expandWidget'),
                    type: GA_CLICK_TYPES.ICON,
                    region: GA_REGIONS.WIDGET_BODY,
                    action: t('gaTracking.ecaHomePage.modalLabelwidget', {
                      label: t('gaTracking.ecaHomePage.exports'),
                    }),
                  },
                });
                setIsOpen(true);
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                  subPage: {
                    subpage_name: t('gaTracking.ecaHomePage.subpageWidget', {
                      label: t('gaTracking.ecaHomePage.exports'),
                    }),
                    subpage_type: GA_SUBPAGE_TYPE.MODAL,
                  },
                });
              }}
              className="expand-button"
              aria-label={`${t('ecaHomepageModals.exportModal.goToExportManagement')} ${t('buttons.expand')}`}
              aria-describedby='exports-expand-hint'
            >
              <OpenFullScreen />
            </IconButton>
            <TallyDetailsExportDialog
              projectId={projectId}
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
            />
          </div>
        </div>
        <ThemeProvider theme={lightTheme}>
          <CssBaseline />
          <DataGridPro
            rows={exportReport}
            columns={columns}
            disableColumnMenu
            getRowId={(row) => row.id}
            onRowClick={(params: {row: any}) => handleRowClick(params?.row)}
            hideFooter
            className="eca-doc-list-grid"
          />
        </ThemeProvider>
      </div>
    </Grid>
  );
};

export default EcaExports;
